import React from 'react';
import { Box, Typography } from '@mui/material';
import { Account } from '../../../../domain/models/Account';
import { useAdminPreferences } from '../../../../application/hooks/useAdminPreferences';
import { formatDate } from '../../../../../../shared/utils/dateUtils';

interface Props {
  account: Account;
}

interface InfoRowProps {
  label: string;
  value: string;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: '0.05em',
      color: '#434656',
      textTransform: 'uppercase',
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 3, py: '10px', borderBottom: '1px solid #f1f5f9' }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#64748b', minWidth: 160 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#0b1c30' }}>{value}</Typography>
  </Box>
);

export const AccountDetailsAccountTab: React.FC<Props> = ({ account }) => {
  const { owner, locale, createdAt, updatedAt } = account;
  const { language, timezone } = useAdminPreferences();

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          p: 3,
          bgcolor: '#ffffff',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
        }}
      >
        <SectionTitle>Identity</SectionTitle>
        <InfoRow label="Owner Name" value={owner?.name ?? '—'} />
        <InfoRow label="Owner Email" value={owner?.email ?? '—'} />
        <InfoRow label="Owner Phone" value={owner?.phone ?? '—'} />
      </Box>

      <Box
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          p: 3,
          bgcolor: '#ffffff',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
        }}
      >
        <SectionTitle>Locale</SectionTitle>
        <InfoRow label="Region" value={locale?.region ?? '—'} />
        <InfoRow label="Timezone" value={locale?.timezone ?? '—'} />
        <InfoRow label="Language" value={locale ? locale.language : '—'} />
      </Box>

      <Box
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          p: 3,
          bgcolor: '#ffffff',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
        }}
      >
        <SectionTitle>Dates</SectionTitle>
        <InfoRow
          label="Created At"
          value={formatDate(createdAt, { language, timezone, includeTime: true })}
        />
        <InfoRow
          label="Last Modified"
          value={formatDate(updatedAt, { language, timezone, includeTime: true })}
        />
      </Box>
    </Box>
  );
};
